import type { ClipboardEvent, FormEvent, ReactNode } from "react";
import { useState } from "react";
import HeroDemoPlayer, { type LandingDemoReplay } from "@/components/marketing/HeroDemoPlayer";
import { SpinnerIcon } from "@/components/marketing/MarketingIcons";
import ScoutMark from "@/components/ScoutMark";

type HeroSectionProps = {
  appBaseUrl: string;
  landingDemo: LandingDemoReplay;
};

export default function HeroSection({ appBaseUrl, landingDemo }: HeroSectionProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedUrl = url.trim();

    if (!trimmedUrl || !validUrl(trimmedUrl)) {
      setError("Please enter a valid URL (e.g., yourproduct.com)");
      return;
    }

    const fullUrl = fullUrlFromInput(trimmedUrl);
    setSubmitting(true);
    setError("");

    const startTime = Date.now();

    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 5000);

      await fetch(fullUrl, {
        method: "HEAD",
        mode: "no-cors",
        signal: controller.signal,
      });

      window.clearTimeout(timeoutId);

      const responseTime = Date.now() - startTime;
      const delay = responseTime < 100 ? 30 : Math.max(0, 1000 - responseTime);

      window.setTimeout(() => {
        window.location.href = `${appBaseUrl}/domains/new?audit_url=${encodeURIComponent(fullUrl)}`;
      }, delay);
    } catch (submitError) {
      setSubmitting(false);

      if (submitError instanceof Error && submitError.name === "AbortError") {
        setError("Website took too long to respond. Please check the URL and try again.");
        return;
      }

      setError("Unable to reach the website. Please check the URL and try again.");
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();
    let value = event.clipboardData.getData("text");

    if (value.startsWith("https://")) {
      value = value.substring(8);
    } else if (value.startsWith("http://")) {
      value = value.substring(7);
    }

    if (value.endsWith("/")) {
      value = value.slice(0, -1);
    }

    setUrl(value);
    setError("");
  }

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-primary-900 to-slate-800">
      <div className="absolute inset-0 opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 25%, rgba(56, 189, 248, 0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(251, 146, 60, 0.3) 0%, transparent 50%)",
          }}
        />
      </div>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-4 -top-4 h-24 w-24 animate-pulse rounded-full bg-gradient-to-br from-secondary-400/20 to-secondary-600/20 blur-xl" />
        <div className="absolute right-1/4 top-1/4 h-16 w-16 animate-pulse rounded-full bg-gradient-to-br from-primary-400/20 to-primary-600/20 blur-lg delay-1000" />
        <div className="absolute bottom-1/4 left-1/3 h-20 w-20 animate-pulse rounded-full bg-gradient-to-br from-cyan-400/20 to-cyan-600/20 blur-xl delay-2000" />
      </div>

      <nav className="relative py-2 pl-6 pr-4 xl:px-0">
        <div className="mx-auto max-w-7xl">
          <div className="flex h-16 items-center justify-between">
            <a href="/" className="flex items-center gap-3 text-2xl font-semibold text-white">
              <ScoutMark variant="glyph" className="h-8 w-auto shrink-0" />
              <span className="-mb-1">ScoutUX</span>
            </a>

            <div className="hidden md:block">
              <div className="ml-10 flex items-center space-x-8">
                <NavLink href="#how-it-works">How it works</NavLink>
                <NavLink href="#pricing">Pricing</NavLink>
                <NavLink href="#faq">FAQ</NavLink>
                <NavLink href={`${appBaseUrl}/login`}>Log in</NavLink>
                <a
                  href={`${appBaseUrl}/domains/new`}
                  className="rounded-lg bg-gradient-to-r from-secondary-500 to-secondary-600 px-4 py-2 text-sm font-medium text-white transition-all hover:from-secondary-600 hover:to-secondary-700"
                >
                  Start free audit
                </a>
              </div>
            </div>

            <div className="md:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md p-2 text-slate-300 hover:bg-slate-700/50 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                aria-label="Toggle menu"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  {menuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {menuOpen ? (
          <div className="absolute left-0 right-0 top-16 z-50 border-t border-slate-700 bg-slate-800 shadow-2xl md:hidden">
            <div className="space-y-1 px-2 pb-5 pt-2 sm:px-3">
              <MobileNavLink href="#how-it-works" onClick={() => setMenuOpen(false)}>
                How it works
              </MobileNavLink>
              <MobileNavLink href="#pricing" onClick={() => setMenuOpen(false)}>
                Pricing
              </MobileNavLink>
              <MobileNavLink href="#faq" onClick={() => setMenuOpen(false)}>
                FAQ
              </MobileNavLink>
              <MobileNavLink href={`${appBaseUrl}/login`} onClick={() => setMenuOpen(false)}>
                Log in
              </MobileNavLink>
              <a
                href={`${appBaseUrl}/domains/new`}
                className="mt-2 block rounded-lg bg-gradient-to-r from-secondary-500 to-secondary-600 px-3 py-2 text-base font-medium text-white transition-all hover:from-secondary-600 hover:to-secondary-700"
              >
                Start free audit
              </a>
            </div>
          </div>
        ) : null}
      </nav>

      <div className="relative px-4 pb-10 pt-6 sm:px-6 sm:py-28 lg:px-8 xl:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16 xl:gap-20">
            <div className="text-center lg:pr-6 lg:text-left xl:pr-10">
              <p className="mb-3 text-sm font-medium tracking-wide text-secondary-300">For SaaS</p>
              <h1 className="pb-2 text-4xl font-bold leading-tight text-slate-100 sm:text-6xl lg:text-[52px]">
                Find what makes your product harder to use.
              </h1>

              <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-400 sm:text-xl sm:leading-8 lg:max-w-none">
                We’ll use it like a customer and flag friction — confusing steps, or steps that are harder than they
                should be.
              </p>

              <div className="mt-4 hidden flex-wrap justify-center gap-4 sm:flex lg:justify-start">
                {["Clear friction", "Real evidence", "Weekly rechecks"].map((value) => (
                  <div key={value} className="flex items-center gap-2 text-sm text-slate-400">
                    <div className="h-2 w-2 rounded-full bg-secondary-400" />
                    {value}
                  </div>
                ))}
              </div>

              <form className="mt-10 w-full lg:w-auto" onSubmit={handleSubmit}>
                <div className="mx-auto flex w-full flex-col sm:w-[400px] lg:mx-0 lg:w-full">
                  <div className="flex flex-col gap-3 lg:max-w-lg lg:flex-row">
                    <div className="relative mx-auto w-full max-w-full flex-1 sm:mx-auto sm:w-[400px] lg:mx-0 lg:w-[300px]">
                      <div className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-xl text-gray-600">
                        https://
                      </div>
                      <input
                        id="url"
                        className={`w-full rounded-xl border-2 bg-white py-4 pl-22 pr-4 text-xl shadow-sm transition-all placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
                          error
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                            : "border-gray-200 hover:border-gray-300 focus:border-secondary-500 focus:ring-secondary-500/20"
                        }`}
                        type="text"
                        placeholder="yourproduct.com"
                        required
                        value={url}
                        onChange={(event) => {
                          setUrl(event.target.value);
                          setError("");
                        }}
                        onPaste={handlePaste}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="group relative mx-auto flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-xl bg-gradient-to-r from-secondary-500 to-secondary-600 px-6 py-4 text-lg font-semibold text-white shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-secondary-500/25 disabled:cursor-not-allowed disabled:opacity-75 disabled:hover:scale-100 sm:mx-auto sm:w-[400px] lg:min-w-[210px] lg:w-auto"
                      aria-busy={submitting}
                    >
                      {submitting ? (
                        <span className="relative z-10 flex items-center justify-center gap-2 whitespace-nowrap">
                          <SpinnerIcon className="h-5 w-5" />
                          Checking...
                        </span>
                      ) : (
                        <span className="relative z-10 text-center whitespace-nowrap">Start free audit</span>
                      )}
                      <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-secondary-600 to-secondary-700 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    </button>
                  </div>
                  {error ? (
                    <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                      {error}
                    </div>
                  ) : null}
                </div>
              </form>

              <p className="mt-4 text-sm text-slate-300">Free sample audit on your public pages. No credit card.</p>
            </div>

            <div className="relative">
              <div className="relative mx-auto max-w-full sm:max-w-md lg:max-w-none">
                <div className="relative rounded-2xl border border-white/20 bg-gradient-to-br from-white/10 to-white/5 p-3 shadow-2xl backdrop-blur-sm sm:p-6">
                  <HeroDemoPlayer replay={landingDemo} />
                  <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
                    <div className="absolute left-0 top-0 h-0.5 w-full animate-pulse bg-gradient-to-r from-transparent via-secondary-400 to-transparent" />
                    <div className="absolute bottom-0 right-0 h-full w-0.5 animate-pulse bg-gradient-to-b from-transparent via-primary-400 to-transparent delay-1000" />
                  </div>
                </div>
                <div className="absolute -left-8 -top-8 -z-10 h-32 w-32 rounded-full bg-gradient-to-br from-primary-500/30 to-primary-700/30 blur-2xl" />
                <div className="absolute -bottom-8 -right-8 -z-10 h-24 w-24 rounded-full bg-gradient-to-br from-secondary-500/30 to-secondary-700/30 blur-2xl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function NavLink({ children, href }: { children: ReactNode; href: string }) {
  return (
    <a href={href} className="px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-white">
      {children}
    </a>
  );
}

function MobileNavLink({ children, href, onClick }: { children: ReactNode; href: string; onClick: () => void }) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="block px-3 py-2 text-base font-medium text-slate-300 transition-colors hover:text-white"
    >
      {children}
    </a>
  );
}

function fullUrlFromInput(value: string) {
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  return `https://${value}`;
}

function validUrl(value: string) {
  const hasValidDomain =
    /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}/.test(value) ||
    /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}/.test(value);
  const isNotJustText = !/^[a-zA-Z\s]+$/.test(value);
  const hasProperFormat = !value.includes(" ") || value.startsWith("http://") || value.startsWith("https://");

  return hasValidDomain && isNotJustText && hasProperFormat;
}
